// This app runs its own MFA (user_mfa_factors + verify-mfa-v3), which never
// raises the Supabase JWT's `aal` claim - so a completed verification has to be
// recorded by the app itself or nothing survives a reload. sessionStorage keeps
// the marker tab-scoped and self-clearing on tab close; the durable 30-day
// "remember this device" trust stays in the signed HttpOnly cookie managed by
// the manage-device-mfa function, which is the right place for it.
//
// The marker itself carries nothing worth reading: an absolute expiry and a
// one-way digest of the user id, never the id and never anything derived from
// the verification. The digest only has to tell two accounts apart inside a
// single tab.
//
// The key deliberately follows the `figabl_mfa_*` + email convention so
// clearMfaSessionStorage() in AuthContext sweeps it on sign-out along with the
// rest.
const VERIFIED_MARKER_TTL_MS = 12 * 60 * 60 * 1000;

const mfaVerifiedKey = (email: string) => `figabl_mfa_verified_${email}`;

type VerifiedMarker = { sub?: string; exp?: number };

// FNV-1a, 32-bit. Synchronous (crypto.subtle is not) and one-way, which is all
// the account discriminator below asks of it - it is not a secret or a
// credential, and it never leaves this tab.
const digestSubject = (value: string): string => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
};

export const markMfaVerified = (email?: string, userId?: string) => {
  if (!email) return;

  const marker: VerifiedMarker = {
    exp: Date.now() + VERIFIED_MARKER_TTL_MS,
    ...(userId ? { sub: digestSubject(userId) } : {}),
  };

  try {
    sessionStorage.setItem(mfaVerifiedKey(email), JSON.stringify(marker));
  } catch (error) {
    // Storage unavailable (private mode, quota). In-memory state still carries
    // the current tab, so this is not worth failing a verification over.
    console.warn('[mfaVerificationMarker] Could not persist MFA verification marker:', error);
  }
};

export const readMfaVerified = (email?: string, userId?: string): boolean => {
  if (!email) return false;

  try {
    const raw = sessionStorage.getItem(mfaVerifiedKey(email));
    if (!raw) return false;

    const marker = JSON.parse(raw) as VerifiedMarker;

    if (!marker?.exp || Date.now() > marker.exp) {
      sessionStorage.removeItem(mfaVerifiedKey(email));
      return false;
    }

    // A marker written for a different account must never carry this one. A
    // marker with no subject at all (an older shape, or one someone typed in)
    // is not trusted either once we know who is asking.
    if (userId && marker.sub !== digestSubject(userId)) return false;

    return true;
  } catch {
    return false;
  }
};
